import React from 'react';
import {useSelector} from 'react-redux';
import {ForecastMapService} from '../api';
import {ForecastMapConfig} from '../api/types';
import {selectIsPremium} from '../store/billing/selectors';
import type {CommonRootState} from '../store/types';
import {selectIsLoggedIn} from '../store/user/selectors';
import useApiClient from './useApiClient';

type ForecastMapCatalogStatus = 'loading' | 'ready' | 'error';

// Who is asking, as far as the catalog's answer goes: the server locks the
// presets the caller is not entitled to (signed out: all but GFS), so a
// sign-in, a sign-out or a Premium change is a different catalog (R10). A
// string, so a token refresh or an unchanged billing update is no change.
const selectCatalogAudience = (state: CommonRootState) =>
  `${selectIsLoggedIn(state)}:${selectIsPremium(state)}`;

type CatalogResult = {
  // Which load produced this result (reload count, include_locked and the
  // audience); a mismatch means a load is in flight.
  key: string | null;
  status: 'ready' | 'error';
  configs: ForecastMapConfig[];
};

/**
 * Fetches the forecast-map preset catalog (all models — grouping happens
 * client-side so switching models never refetches).
 *
 * `includeLocked` also lists the presets this visitor cannot open, marked
 * `locked: true` (#327 MR7a). Only the unified map asks for them: every
 * other consumer would offer configs whose manifests 404.
 *
 * Signed in or out (GFS for everyone). The answer depends on the caller's
 * entitlement, so it is refetched when the visitor signs in or out or
 * their Premium changes; meanwhile the hook reports `loading`, never the
 * other audience's list.
 *
 * Platform-agnostic on purpose (no DOM usage): backs the webapp screens and
 * the mobile forecast-map screen.
 */
const useForecastMapCatalog = ({
  includeLocked = false,
}: {includeLocked?: boolean} = {}) => {
  const {fetch} = useApiClient();
  const audience = useSelector(selectCatalogAudience);
  const [loadKey, setLoadKey] = React.useState(0);
  const [result, setResult] = React.useState<CatalogResult>({
    key: null,
    status: 'ready',
    configs: [],
  });

  const key = `${loadKey}:${includeLocked}:${audience}`;
  React.useEffect(() => {
    let cancelled = false;
    fetch(
      ForecastMapService.forecastMapConfigList(
        includeLocked ? {include_locked: true} : undefined,
      ),
    )
      .then((configs) => {
        if (!cancelled) {
          setResult({key, status: 'ready', configs});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({key, status: 'error', configs: []});
        }
      });
    // Drop late responses on unmount/reload.
    return () => {
      cancelled = true;
    };
  }, [fetch, key, includeLocked]);

  const reload = React.useCallback(() => setLoadKey((count) => count + 1), []);

  // Derived: any load whose result hasn't landed yet renders as loading.
  const loading = result.key !== key;
  const status: ForecastMapCatalogStatus = loading ? 'loading' : result.status;

  return {status, configs: loading ? [] : result.configs, reload};
};

export default useForecastMapCatalog;
